import { refreshSession, signOut } from "@/services/auth";
import * as Sentry from "@sentry/react";

const getTokenFingerprint = (token: string): string =>
  token ? `${token.slice(0, 12)}...${token.slice(-8)}` : "(none)";

// Public API endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ["/agreements/questionnaire"];

const addAPIKey = (
  apiKey: string,
  request: RequestInfo | URL,
  config: RequestInit = {},
  impersonateCognitoId?: string,
) => {
  if (!config.headers) {
    config.headers = {};
  }

  const headers: Record<string, string> = {
    "X-API-key": apiKey,
  };

  if (impersonateCognitoId) {
    headers["X-Impersonate-Cognito-Id"] = impersonateCognitoId;
  }

  Object.assign(config.headers, headers);

  return [request, config] as const;
};

const isPublicEndpoint = (request: RequestInfo | URL): boolean => {
  try {
    let pathname: string;
    if (request instanceof Request) {
      pathname = new URL(request.url).pathname;
    } else if (request instanceof URL) {
      pathname = request.pathname;
    } else {
      pathname = new URL(request, window.location.origin).pathname;
    }
    return PUBLIC_ENDPOINTS.some(
      (endpoint) =>
        pathname === endpoint || pathname.startsWith(endpoint + "/"),
    );
  } catch {
    return false;
  }
};

let refreshPromise: Promise<string> | null = null;
const getNewAPIKey = async () => {
  Sentry.captureMessage("[AR-1810] AuthInterceptor: getNewAPIKey: START", {
    level: "info",
    tags: { ar1810: true },
  });
  if (refreshPromise) {
    Sentry.captureMessage(
      "[AR-1810] AuthInterceptor: getNewAPIKey: DEDUP (existing refresh in progress)",
      { level: "info", tags: { ar1810: true } },
    );
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      const session = await refreshSession();
      const newToken = session.getIdToken().getJwtToken();
      Sentry.captureMessage(
        "[AR-1810] AuthInterceptor: getNewAPIKey: SUCCESS",
        {
          level: "info",
          tags: { ar1810: true },
          extra: { tokenFingerprint: getTokenFingerprint(newToken) },
        },
      );
      return newToken;
    } catch (err) {
      Sentry.captureMessage("[AR-1810] AuthInterceptor: getNewAPIKey: FAILED", {
        level: "error",
        tags: { ar1810: true },
        extra: {
          errorName: (err as Error).name,
          errorMessage: (err as Error).message,
        },
      });
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

export const AuthInterceptor = (
  idToken: string,
  getImpersonateCognitoId?: () => string | null,
) => {
  Sentry.captureMessage("[AR-1810] AuthInterceptor: INIT", {
    level: "info",
    tags: { ar1810: true },
    extra: { tokenFingerprint: getTokenFingerprint(idToken) },
  });

  // Store the original fetch function
  const originalFetch = window.fetch;

  // Initialize the API key with the provided idToken.
  // It will be updated if the session is refreshed.
  let apiKey = idToken;

  // Override the global fetch function
  window.fetch = async (...args) => {
    const [request, config] = args;

    // Bypacss authentication for public endpoints
    if (isPublicEndpoint(request)) {
      return originalFetch(request, config);
    }

    const impersonateCognitoId = getImpersonateCognitoId?.() || undefined;
    const [requestWithAuth, configWithAuth] = addAPIKey(
      apiKey,
      request,
      config,
      impersonateCognitoId,
    );
    const response = await originalFetch(requestWithAuth, configWithAuth);

    // For unauthenticated users, attempt to refresh the session
    if (response.status === 401) {
      Sentry.captureMessage(
        `[AR-1810] AuthInterceptor: 401 received — URL: ${isPublicEndpoint(request) ? "(public)" : request}`, // eslint-disable-line @typescript-eslint/no-unsafe-member-access
        {
          level: "warning",
          tags: { ar1810: true },
          extra: {
            currentApiKeyFingerprint: getTokenFingerprint(apiKey),
            isPublicEndpoint: isPublicEndpoint(request),
          },
        },
      );
      try {
        apiKey = await getNewAPIKey();
        Sentry.captureMessage(
          `[AR-1810] AuthInterceptor: 401 retry — new apiKey: ${getTokenFingerprint(apiKey)}`,
          {
            level: "info",
            tags: { ar1810: true },
          },
        );
        const [retryRequest, retryConfig] = addAPIKey(
          apiKey,
          request,
          config,
          impersonateCognitoId,
        );
        const retryResponse = await originalFetch(retryRequest, retryConfig);
        if (retryResponse.status === 401) {
          Sentry.captureMessage(
            "[AR-1810] AuthInterceptor: 401 retry FAILED — second 401, calling signOut",
            {
              level: "error",
              tags: { ar1810: true },
              extra: {
                reason: "double-401-after-refresh",
              },
            },
          );
          signOut();
          return retryResponse;
        }
        return retryResponse;
      } catch (refreshError) {
        Sentry.captureMessage(
          "[AR-1810] AuthInterceptor: 401 refresh threw — calling signOut",
          {
            level: "error",
            tags: { ar1810: true },
            extra: {
              reason: "refresh-error",
              errorName: (refreshError as Error).name,
              errorMessage: (refreshError as Error).message,
            },
          },
        );
        signOut();
        return response;
      }
    }

    // Return the response for successful calls or non-401 errors
    return response;
  };

  // Cleanup function to restore the original fetch
  return () => {
    window.fetch = originalFetch;
  };
};
